import { computed, watch } from 'vue';
import type { Ref } from 'vue';
import { useEndpoint } from './useEndpoint';
import { useOpportunities } from './useOpportunities';
import { useUtilities } from './useUtilities';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import {
  AssociationAccount,
  AssociationContact,
  CreateOpportunityPayload,
  CreateOpportunityResponse,
  OpportunityDetailModel,
  OpportunityType,
  OpportunityUpdatePayload,
  OpportunityUpdateBody,
  Opportunity,
} from '@apparatix/types/Leads';
import { useOpportunityAssociations } from './useOpportunityAssociations';

export const useCreateOpportunity = (type: OpportunityType, accounts: Ref<AssociationAccount[]>, contacts: Ref<AssociationContact[]>) => {
  const store = useOpportunityStore();

  const isConvertingLead = computed(() => store.createOpportunityContext === 'convert-lead');

  const variableType = computed<OpportunityType>(() => {
    if (isConvertingLead.value) return 'Opportunity';
    return type;
  });

  const { isLoading, makeRequest } = useEndpoint<CreateOpportunityResponse>({
    type: 'POST',
    url: '/crm/opportunities',
    successField: 'data',
    onSuccess: response => {
      store.addOpportunity(response.data.opportunity, true);
      store.selectedCampaignId['Opportunity'] = response.data.opportunity.id;
      store.shouldShowDetail['Opportunity'] = true;
    },
    toast: { showOnSuccess: false },
  });

  const { standardizeObjectForPutRequest } = useOpportunities();
  const {
    addAssociationsToRequestBody,
    areAccountsValid,
    areContactsValid,
  } = useOpportunityAssociations(type, accounts, contacts);
  const { getOnlyPopulatedFields } = useUtilities();

  const updateEndpoint = useEndpoint<OpportunityUpdatePayload>({
    url: '/crm/opportunities',
    type: 'PUT',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const creatingLead = computed(() => type === 'Lead');

  const isAbleToSave = computed(() =>
    areAccountsValid.value &&
    areContactsValid.value,
  );

  watch(contacts, (newValue, oldValue) => {
    if (creatingLead.value) return;
    if (newValue?.length === 1 && oldValue?.length === 0) {
      const primaryLabel = store.options['Opportunity'].contactLabels?.find(label => label.contactLabelDisplayName === 'Primary');
      if (!primaryLabel || !contacts.value[0]) return;
      contacts.value[0].contactLabels = [primaryLabel];
    }
  }, { deep: true, once: true });

  const createUpdatePayload = (details?: OpportunityDetailModel): OpportunityUpdatePayload => {

    const opportunityData = getOnlyPopulatedFields(details.opportunity);

    const body: OpportunityUpdateBody = {
      ...opportunityData,
      contacts: mapContacts() as Array<{ id: number, labelIds: number[] }>,
    };

    if (opportunityData.status && opportunityData.statusId) {
      (body as any).status = opportunityData.statusId;
    }

    const revenueData = getOnlyPopulatedFields(details.revenueData);
    if (revenueData) {
      body.revenueData = revenueData;
    }

    const plan = getOnlyPopulatedFields(details.planData);
    if (plan) {
      body.planData = plan;
    }

    addAssociationsToRequestBody(body);
    standardizeObjectForPutRequest(body);
    body.type = 'Opportunity';

    return { data: { opportunity: [body]}};
  };

  const createCreationPayload = (details?: OpportunityDetailModel): CreateOpportunityPayload => {
    if (details) {
      standardizeObjectForPutRequest(details, true);
    }
    const payload: CreateOpportunityPayload = { data: { opportunity: {}}};

    const populatedOpportunityDetails = getOnlyPopulatedFields(details?.opportunity);
    if (populatedOpportunityDetails) {
      if (populatedOpportunityDetails.status && populatedOpportunityDetails.statusId) {
        (populatedOpportunityDetails as any).status = populatedOpportunityDetails.statusId;
      }
      payload.data.opportunity = { ...populatedOpportunityDetails, ...payload.data.opportunity };
    }

    const populatedPlanData = getOnlyPopulatedFields(details.planData);
    if (populatedPlanData) {
      payload.data.opportunity.planData = populatedPlanData;
    }

    const populatedRevenueData = getOnlyPopulatedFields(details?.revenueData);
    if (populatedRevenueData) {
      payload.data.opportunity.revenueData = populatedRevenueData;
    }

    if (contacts.value.length) {
      payload.data.opportunity.contacts = mapContacts();
    }

    if (accounts.value?.length) {
      addAssociationsToRequestBody(payload.data.opportunity);
    }

    return payload;
  };

  const mapContacts = (): Array<{ id: number, labelIds?: number[] }> => contacts.value.map(contact => {
    const c: { id: number, labelIds?: number[] } = { id: contact.id };
    if (contact.contactLabels?.length) {
      c.labelIds = contact.contactLabels.map(label => label.contactLabelId);
    }
    return c;
  });

  const handleSave = async (details?: OpportunityDetailModel, skipValidityCheck = false): Promise<Opportunity> => {
    if (!isAbleToSave.value && !skipValidityCheck) return;

    if (isConvertingLead.value) {
      const payload = createUpdatePayload(details);
      await updateEndpoint.makeRequest(payload);
    } else {
      const payload = createCreationPayload(details);
      await makeRequest(payload);
    }
  };

  const cleanup = () => {
    contacts.value = [];
    accounts.value = [];
  };

  return {
    accounts,
    cleanup,
    contacts,
    handleSave,
    isAbleToSave,
    isLoading,
    variableType,
  };
};
